import { makeAutoObservable, runInAction } from 'mobx'
import { orderMobx, fraction } from '..'
import { toDecimal } from '@dt/fe-utils'
import { minBy, maxBy, sumBy, orderBy, each, isEmpty, some, keyBy, cloneDeep, values, groupBy, flatten, keys } from '@dt/fe-utils/dist/lodash'
import SplitAPI from 'api/split.api'
import OrderAPI from 'api/order.api'

type TId = number | string
type TDistributeItemObj = {
  [key in TId]: IProductDetail;
}

interface IOrderFeeInfo {
  originalFee: number;
  paidFee: number;
  tax: number;
  index: number;
  add?: boolean;
  details?: any;
}

interface ISetedItemDetail {
  /** 商品是否全都分配到子单上的标志位 */
  cnt: number;
  name?: string;
  type?: 'product' | 'serviceCharge' | 'modify' | 'money';
  detailId: string;
  originalFee: number;
  paidFee: number;
  tax: number;
  paiedPrice: number;
  isFull: boolean;
  isPaied: boolean;
  productExt?: string;
  detailList: any[];
}

type TSetedItems = {
  [key in TId]: ISetedItemDetail;
}

class SplitOrder {
  constructor () {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  /** 分单列表 */
  splitOrderList: ISubOrder[] = []

  /** 勾选的待分配的项 */
  selectedDistributeItem: TDistributeItemObj = {}

  /** 待拆分的项 */
  selectedSplitInfo = {} as IProductDetail

  /** 已支付金额 */
  paidedFee: number = 0

  /** 正在支付的card */
  payingCard: string = ''

  /** 是否按座位 */
  isSplitBySeat: boolean = false

  /** 当前分单类型 */
  splitType: string = ''

  /** 自动分服务费 */
  isAutoSplitServiceCharge: boolean = false

  /** 座位分单数据缓存 */
  cacheSeatSubOrders: any[] = []

  /** 待分配金额 */
  get restAmount () {
    let totalPrice = sumBy(this.splitOrderList, (item) => (item.paidFee || 0) + (item.tax || 0))
    let paidFee = sumBy(this.splitOrderList, (item) => item.paidFee || 0)
    let tax = sumBy(this.splitOrderList, (item) => item.tax || 0)
    return {
      totalPrice: toDecimal(this.mainOrderInfo.totalPrice - toDecimal(totalPrice, 3)),
      paidFee: toDecimal(this.mainOrderInfo.paidFee - toDecimal(paidFee, 3)),
      tax: toDecimal(this.mainOrderInfo.tax - toDecimal(tax, 3)),
    }
  }

  /** 主订单信息 */
  get mainOrderInfo () {
    const info = orderMobx.viewOrder
    // 分单总价 = 总价 - 已支付附加费
    const totalPrice = info.totalPrice
    const serviceChargeTax = (orderMobx.order.detailList?.filter(i => i.itemType === 'serviceCharge')).reduce((t: number, i) => t + (i.tax || 0), 0)
    const expressTax = (orderMobx.order.detailList?.filter(i => i.itemType === 'express')).reduce((t: number, i) => t + (i.tax || 0), 0)
    const orderInfo = {
      ordersId: info.ordersId,
      nono: info.nono,
      totalOriginalPrice: info.totalOriginalPrice,
      totalPrice: totalPrice,
      paidFee: info.paidFee,
      originalFee: info.originalFee,
      unPaidFee: toDecimal(totalPrice - this.paidedFee),
      detailList: info.detailList,
      serviceChargeGratuityName: info.serviceChargeGratuityName,
      serviceChargeDetailId: info.serviceChargeDetailId,
      serviceCharge: toDecimal(info.serviceCharge + serviceChargeTax),
      serviceChargeTax: serviceChargeTax,
      addUserId: info.addUserId,
      addUserName: info.addUserName,
      tax: info.tax || 0,
      tableCode: info.tableCode,
      tip: info.tip || 0,
      surcharge: info.surcharge,
      express: toDecimal(info.expressPrice + expressTax),
      expressTax
    } as IMainOrder
    return orderInfo
  }

  /** 服务费信息 */
  get mainServiceChargeInfo () {
    const serviceCharge = this.productItemList.find(p => p.type === 'serviceCharge')
    return {
      serviceCharge: (serviceCharge?.paidFee || 0) + (serviceCharge?.tax || 0),
      serviceChargeTax: serviceCharge?.tax || 0
    }
  }

  /** 右侧商品列表 */
  get productItemList () {
    const detailList = cloneDeep(orderMobx.order.detailList)
      .filter(item => item.cnt > 0)
      .filter(item => !['tip', 'surcharge'].includes(item.itemType))
      .filter(item => item.itemType !== 'express' || (item.itemType === 'express' && (item.paidFee || item.tax)))
    const productList = detailList.filter(item => item.itemType !== 'serviceCharge')
    const serviceChargeList = detailList.filter(item => item.itemType === 'serviceCharge')
    const productGroups: any = groupBy(productList, 'parentId')

    // 餐品
    const list: any[] = productList
      .filter(item => item.parentId === '0')
      .map(item => {
        let _detailList: IOrderDetail[] = []
        let _childList: any = productGroups?.[item.detailId] || []
        if (['FixedCombo', 'OptionalCombo'].includes(item.itemType)) {
          _detailList = [cloneDeep(item), ...cloneDeep(_childList)] as IOrderDetail[]
          _childList.forEach((c: any) => {
            _detailList.push(...(cloneDeep((productGroups as any)?.[c.detailId]) || [] as IOrderDetail[]))
          })
        } else {
          _detailList = [cloneDeep(item), ...cloneDeep(_childList)] as IOrderDetail[]
        }
        const _clacDetailList = _detailList.filter(item => !['FixedCombo', 'OptionalCombo'].includes(item.itemType))
        const originalFee = sumBy(_clacDetailList, item => item.originalFee || 0)
        const paidFee = sumBy(_clacDetailList, item => item.paidFee || 0)
        const tax = sumBy(_clacDetailList, item => item.tax || 0)
        return {
          type: item.itemType,
          name: item.productName,
          detailId: item.detailId,
          cnt: item.cnt,
          originalFee: toDecimal(originalFee),
          paidFee: toDecimal(paidFee),
          tax: toDecimal(tax),
          detailList: _detailList,
          seat: item.seat,
        }
      })

    // 服务费
    if (serviceChargeList?.length) {
      const originalFee = sumBy(serviceChargeList, item => item.originalFee || 0)
      const paidFee = sumBy(serviceChargeList, item => item.paidFee || 0)
      const tax = sumBy(serviceChargeList, item => item.tax || 0)
      list.push({
        type: 'serviceCharge',
        name: this.mainOrderInfo.serviceChargeGratuityName,
        detailId: this.mainOrderInfo.serviceChargeDetailId,
        cnt: 1,
        originalFee: toDecimal(originalFee),
        paidFee: toDecimal(paidFee),
        tax: toDecimal(tax),
        detailList: cloneDeep(serviceChargeList),
        seat: null,
      })
    }

    // 处理餐品金额与总金额不等的情况
    let itemTotal = sumBy(list, (item) => (item.paidFee || 0) + (item.tax || 0))
    let diff = toDecimal(this.mainOrderInfo.totalPrice - itemTotal - this.mainOrderInfo.tip - this.mainOrderInfo.surcharge)
    let i = 0
    let negative = diff > 0 ? 1 : -1
    while (Math.abs(diff) >= 0.01) {
      if (list[i] && ((list[i].paidFee || 0) + (list[i].tax || 0)) > 0.01) {
        list[i].paidFee = toDecimal(list[i].paidFee + 0.01 * negative)
        diff = toDecimal(diff - 0.01 * negative)
      }
      i = i === list.length ? 0 : i + 1
    }

    return list
  }

  get productItemMap () {
    return keyBy(this.productItemList, 'detailId')
  }

  /** 获取已分配的餐品（包括服务费） */
  get settedOrderItems () {
    const items = {} as TSetedItems
    let findItem
    this.splitOrderList.forEach((item) => {
      const detailList = item.subOrdersDetail
        ?.filter(item => !['tip', 'surcharge'].includes(item.itemType))
      const productList = detailList?.filter(item => item.itemType !== 'serviceCharge')
      const serviceChargeList = detailList?.filter(item => item.itemType === 'serviceCharge')
      const productGroups: any = groupBy(productList, 'parentId')

      item.subOrdersDetail?.forEach((detail) => {
        if (!detail.productExt) {
          return
        }
        if (!items[detail.detailId]) {
          items[detail.detailId] = {
            paiedPrice: 0,
            originalFee: 0,
            paidFee: 0,
            tax: 0,
            isFull: false,
            isPaied: false,
            cnt: detail.cnt || 1,
            detailId: detail.detailId,
            detailList: []
          }
        }
        // 餐品子项
        let _detailList: ISubOrdersDetail[] = []
        let _childList: any = productGroups?.[detail.detailId] || []
        if (['FixedCombo', 'OptionalCombo'].includes(detail.itemType)) {
          _detailList = [cloneDeep(detail), ...cloneDeep(_childList)] as ISubOrdersDetail[]
          _childList.forEach((c: any) => {
            _detailList.push(...(cloneDeep((productGroups as any)?.[c.detailId]) || [] as ISubOrdersDetail[]))
          })
        } else {
          _detailList = [cloneDeep(detail), ...cloneDeep(_childList)] as ISubOrdersDetail[]
        }
        let _clacDetailList = _detailList.filter(item => !['FixedCombo', 'OptionalCombo'].includes(item.itemType))
        let sumOriginalFee = sumBy(_clacDetailList, item => item.originalFee || 0)
        let sumPaidFee = sumBy(_clacDetailList, item => item.paidFee || 0)
        let sumTax = sumBy(_clacDetailList, item => item.tax || 0)

        // 服务费
        if (detail.itemType === 'serviceCharge' && detail.detailId === this.mainOrderInfo.serviceChargeDetailId) {
          sumOriginalFee = sumBy(serviceChargeList, item => item.originalFee || 0)
          sumPaidFee = sumBy(serviceChargeList, item => item.paidFee || 0)
          sumTax = sumBy(serviceChargeList, item => item.tax || 0)
        }

        if (item.status === 'Success') {
          items[detail.detailId].paiedPrice = toDecimal(
            items[detail.detailId].paiedPrice + sumPaidFee + sumTax
          )
        }

        items[detail.detailId].detailList = [
          ...items[detail.detailId].detailList,
          ..._detailList
        ]

        items[detail.detailId].originalFee = toDecimal(
          items[detail.detailId].originalFee + sumOriginalFee
        )

        items[detail.detailId].paidFee = toDecimal(
          items[detail.detailId].paidFee + sumPaidFee
        )

        items[detail.detailId].tax = toDecimal(
          items[detail.detailId].tax + sumTax
        )

        items[detail.detailId].productExt = fraction.subtract(items[detail.detailId].productExt || '1/1', detail.productExt)
      })
    })

    this.productItemList.forEach((item) => {
      findItem = items[item.detailId]
      if (findItem) {
        findItem.name = item.name
        findItem.isFull = toDecimal((findItem.paidFee || 0) + (findItem.tax || 0)) === toDecimal((item.paidFee || 0) + (item.tax || 0))
        findItem.isPaied = toDecimal(findItem.paiedPrice) === toDecimal((item.paidFee || 0) + (item.tax || 0))
      }
    })

    return items
  }

  /** 已分单类型 */
  get splitedType () {
    if (this.restAmount.totalPrice === this.mainOrderInfo.totalPrice) {
      return ''
    }
    return isEmpty(this.settedOrderItems) ? 'money' : 'product'
  }

  get splitOrderMap () {
    return keyBy(this.splitOrderList, 'ordersId')
  }

  init () {
    return (SplitAPI as any).getSplitOrders({
      ordersId: orderMobx.viewOrder.ordersId,
    }).then((res: any) => {
      if (res?.result) {
        const result = this.sortListByNono(res?.result)
        runInAction(() => {
          this.splitOrderList = result
          const paidedList = result.filter((item) => item.status === 'Success')
          this.paidedFee = toDecimal(
            sumBy(paidedList, (item) => (item.paidFee || 0) + (item.tax || 0))
          )
        })
        return result
      }
      return []
    })
  }

  /** 设置座位分单数据缓存 */
  setCacheSeatSubOrders (subOrders?: any[]) {
    this.cacheSeatSubOrders = subOrders || []
  }

  /** 已退款的子单不能void */
  getOrderPaymentRefundList (nono: string) {
    const findItem = orderMobx.viewOrder.subList?.find(item => item.nono === nono)
    return findItem ? findItem.paymentRefundList : []
  }

  /** 列表根据nono排序 */
  sortListByNono (list: ISubOrder[]) {
    list.forEach((item) => {
      item.subOrdersDetail = item.detailList
      if (item.extList?.length) {
        const seatInfo = item.extList.find(item => item.attrName === 'seat')
        item.seat = seatInfo.attrValue
      }
    })
    return orderBy(list, (item) => parseInt(item.nono.split('-')[1]))
  }

  /** 生成空的子单明细 */
  getEmptySubOrdersDetail () {
    const subOrdersDetail = flatten(this.productItemList.map(item => item.detailList)).map((item) => {
      return {
        tax: 0,
        cnt: 0,
        weight: 0,
        paidFee: 0,
        productExt: '',
        originalFee: 0,
        isTogo: item.isTogo,
        itemType: item.type,
        detailId: item.detailId,
      }
    }) as ISubOrdersDetail[]

    return subOrdersDetail
  }

  /** 过滤分单列表 */
  filterSplitList (status: TPayStatus) {
    return this.splitOrderList.filter((item) => {
      if (status === 'WaitPay') {
        return item.status === 'Pending' && toDecimal((item.paidFee || 0) + (item.tax || 0)) > 0
      }
      return item.status === status
    })
  }

  /** 创建空白的子单 */
  async initialSplitList (cnt: number) {
    const paidedList = this.filterSplitList('Success')
    const subOrders: ISubOrder[] = []
    // 防止和已支付单的nono重复
    let nonoAray = new Array(cnt)
      .fill('')
      .map((_item, index) => `${this.mainOrderInfo.nono}-${index + 1}`)
    nonoAray = nonoAray.filter(
      (nono) => !paidedList.find((item) => item.nono === nono)
    )

    new Array(cnt - paidedList.length).fill('').forEach((_item, index) => {
      subOrders.push({
        tax: 0,
        cnt: 0,
        paidFee: 0,
        originalFee: 0,
        status: 'Pending',
        parentId: null,
        nono: nonoAray[index],
        subOrdersDetail: this.getEmptySubOrdersDetail(),
      })
    })
    try {
      const res = (await (SplitAPI as any).createUpdateSplitOrders({
        subOrders,
        ordersId: this.mainOrderInfo.ordersId,
        addUersId: this.mainOrderInfo.addUserId,
        addUserName: this.mainOrderInfo.addUserName,
      })) as any
      if (res?.result) {
        runInAction(() => {
          this.splitOrderList = this.sortListByNono(res?.result)
        })
        if (this.isSplitBySeat) {
          this.initSplitBySeatList().catch((err: any) => {
            return Promise.reject(err)
          })
        }
        return res?.result
      } else {
        return Promise.reject(res.message)
      }
    } catch (error) {
      return Promise.reject(error.message)
    }
  }

  /** 初始化按座位分单 */
  async initSplitBySeatList () {
    const seatOders: any[] = []
    const items: any = {}
    each(this.productItemList, item => {
      if (item?.seat && item?.seat !== '-1') {
        items[item.seat] = items[item.seat] || { seat: item.seat, children: [] }
        each(item.detailList, detail => {
          items[item.seat].children.push(detail)
        })
      }
    })
    Object.values(items).forEach((item: any, index: number) => {
      let allOriginalFee = 0
      let allPaidFee = 0
      let allTax = 0
      const details = {} as any
      item.children.forEach((detail: any) => {
        if (!['FixedCombo', 'OptionalCombo'].includes(detail.itemType)) {
          allOriginalFee += (detail.originalFee || 0)
          allPaidFee += (detail.paidFee || 0)
          allTax += (detail.tax || 0)
        }
        details[detail.detailId] = {
          cnt: detail.cnt,
          productExt: '1',
          originalFee: detail.originalFee || 0,
          paidFee: detail.paidFee || 0,
          tax: detail.tax || 0,
          weight: toDecimal((detail.paidFee || 0) + (detail.tax || 0)),
        }
      })
      this.setCacheSeatSubOrders(seatOders)
      seatOders.push({ ...this.getSplitOrderFee([{ originalFee: toDecimal(allOriginalFee), paidFee: toDecimal(allPaidFee), tax: toDecimal(allTax), index: index, add: true, details }])[0], seat: item.seat })
    })
    this.setCacheSeatSubOrders()
    try {
      const res = (await (SplitAPI as any).createUpdateSplitOrders({
        subOrders: seatOders,
        ordersId: this.mainOrderInfo.ordersId,
        addUersId: this.mainOrderInfo.addUserId,
        addUserName: this.mainOrderInfo.addUserName,
      })) as any
      if (res?.result) {
        runInAction(() => {
          this.splitOrderList = this.sortListByNono(res?.result)
        })
        return res?.result
      } else {
        return Promise.reject(res.message)
      }
    } catch (error) {
      return Promise.reject(error.message)
    }
  }

  /** 只修改子单不做请求 */
  getSplitOrderFee (orderFees: IOrderFeeInfo[], isEdit?: boolean) {
    let itemDetail
    let findDetail
    let totals = {
      taxTotal: sumBy(this.splitOrderList, (item) => item.tax || 0),
      paidFeeTotal: sumBy(this.splitOrderList, (item) => item.paidFee || 0),
      originalFeeTotal: sumBy(this.splitOrderList, (item) => item.originalFee || 0),
    }
    const isExpress = some(orderFees, o => some(keys(o.details), d => this.productItemMap?.[d]?.type === 'express'))
    const isAutoSplitServiceCharge = this.isAutoSplitServiceCharge && !isExpress

    console.log('orderFees: =>', cloneDeep(orderFees))

    const subOrders = orderFees.map((item) => {
      const subOrder = this.returnSplitOrderFee({
        originalFee: item.originalFee,
        paidFee: item.paidFee,
        tax: item.tax || 0,
        index: item.index,
        totals,
        add: item.add
      }, isAutoSplitServiceCharge)

      console.log('subOrder: =>', cloneDeep(subOrder))

      if (item.details && subOrder.subOrdersDetail) {
        for (let detailId in item.details) {
          itemDetail = item.details[detailId]
          findDetail = subOrder.subOrdersDetail.find(
            (item) => detailId === item.detailId
          )
          if (findDetail) {
            findDetail.originalFee = toDecimal(itemDetail.originalFee || 0)
            findDetail.paidFee = toDecimal(itemDetail.paidFee || 0)
            findDetail.tax = toDecimal(itemDetail.tax || 0)
            findDetail.cnt = itemDetail.cnt || 1
            findDetail.weight = toDecimal(
              (item.add ? (findDetail.weight || 0) : 0) + (itemDetail.weight || 0)
            )
            findDetail.productExt = item.add ? fraction.add(itemDetail.productExt, findDetail.productExt) : itemDetail.productExt
          }
        }
      }

      if (isAutoSplitServiceCharge) {
        subOrder.subOrdersDetail
          ?.filter(item => item.itemType === 'serviceCharge')
          ?.forEach(item => {
            item.cnt = ~~!isEmpty(this.settedOrderItems)
            item.originalFee = 0
            item.paidFee = 0
            item.tax = 0
            item.weight = 0
          })
      }

      if (isEdit && subOrder.subOrdersDetail) {
        subOrder.subOrdersDetail.forEach((detail) => {
          detail.weight = 0
          detail.productExt = ''
        })
      }

      totals = {
        originalFeeTotal:
          (totals.originalFeeTotal || 0) - (this.splitOrderList?.[item.index]?.originalFee || 0) + (subOrder.originalFee || 0),
        paidFeeTotal:
          (totals.paidFeeTotal || 0) - (this.splitOrderList?.[item.index]?.paidFee || 0) + (subOrder.paidFee || 0),
        taxTotal:
          (totals.taxTotal || 0) - (this.splitOrderList?.[item.index]?.tax || 0) + (subOrder.tax || 0),
      }
      return subOrder
    })

    console.log(11, cloneDeep(subOrders))

    this.setSubOrderDetailListFee(subOrders, isAutoSplitServiceCharge)

    console.log(12, cloneDeep(subOrders))

    return subOrders
  }

  /** 清空未支付的子单/置空 */
  clearSplitList (list?: ISubOrder[]) {
    if (list) {
      this.splitOrderList = list
      return
    }
    return (SplitAPI as any).deleteSubOrder({
      ordersId: this.mainOrderInfo.ordersId,
      subOrders: this.filterSplitList('Pending').map((item) => ({
        ordersId: item.ordersId,
      })),
    })
  }

  /** 选择待分配金额 */
  setSelectedDistributeItem (item: IProductDetail) {
    if (!item.paidFee) {
      return
    }
    if (this.selectedDistributeItem[item.detailId]) {
      delete this.selectedDistributeItem[item.detailId]
    } else {
      this.selectedDistributeItem[item.detailId] = {
        ...item,
        paidFee: item.paidFee,
      }
    }
  }

  /** 重置待分配的项 */
  resetDistributeItem () {
    this.selectedDistributeItem = {}
  }

  /** 选择待拆分的项 */
  setSelectedSplitInfo (item: IProductDetail) {
    this.selectedSplitInfo = item
  }

  /** 均分金额（数组） */
  getSplitMount (originalFee: number = 0, paidFee: number = 0, tax: number = 0, count: number, reverse: boolean) {
    let i = 0
    const aveOriginalFee = originalFee
      ? parseFloat((Math.floor((originalFee / count) * 100) / 100).toFixed(2))
      : 0
    const avePaidFee = paidFee
      ? parseFloat((Math.floor((paidFee / count) * 100) / 100).toFixed(2))
      : 0
    const aveTax = tax
      ? parseFloat((Math.floor((tax / count) * 100) / 100).toFixed(2))
      : 0
    const amountArray = new Array(count)

    while (i < count) {
      amountArray[i++] = { originalFee: aveOriginalFee, paidFee: avePaidFee, tax: aveTax }
    }
    const restTotal = toDecimal(paidFee - avePaidFee * count)
    if (restTotal >= 0.01) {
      i = 0
      while (i < toDecimal(restTotal * 100)) {
        amountArray[i].originalFee = toDecimal((amountArray?.[i]?.originalFee || 0) + 0.01)
        amountArray[i].paidFee = toDecimal((amountArray?.[i]?.paidFee || 0) + 0.01)
        amountArray[i].tax = toDecimal((amountArray?.[i]?.tax || 0) + 0.01)
        i++
      }
    }
    amountArray.forEach(item => {
      if (!originalFee) item.originalFee = 0
      if (!paidFee) item.paidFee = 0
      if (!tax) (item.tax = 0)
    })
    const allOriginalFee = amountArray?.reduce((t: number, i) => t + (i.originalFee || 0), 0)
    const allPaidFee = amountArray?.reduce((t: number, i) => t + (i.paidFee || 0), 0)
    const allTax = amountArray?.reduce((t: number, i) => t + (i.tax || 0), 0)
    // 溢出处理
    const overItem = allPaidFee > paidFee ? maxBy(reverse ? amountArray.reverse() : amountArray, a => a.paidFee + a.tax) : minBy(reverse ? amountArray.reverse() : amountArray, a => a.paidFee + a.tax)
    if (allOriginalFee !== originalFee) {
      overItem.originalFee = toDecimal((overItem?.originalFee || 0) - toDecimal(allOriginalFee - originalFee))
    }
    if (allPaidFee !== paidFee) {
      overItem.paidFee = toDecimal((overItem?.paidFee || 0) - toDecimal(allPaidFee - paidFee))
    }
    if (allTax !== tax) {
      overItem.tax = toDecimal((overItem?.tax || 0) - toDecimal(allTax - tax))
    }
    return amountArray
  }

  /** 平分求余 */
  getAverageMount (amount: number, count: number) {
    const averMount = parseFloat(
      (Math.floor((amount / count) * 100) / 100).toFixed(2)
    )
    const total = toDecimal(averMount * count)
    const rest = toDecimal(amount - total)

    return [averMount, rest]
  }

  /** 平均分配剩余金额 */
  async averageSplit (count: number) {
    let subOrders = [] as ISubOrder[]
    const { originalFee, paidFee, tax } = this.mainOrderInfo

    const originalFeeArray = this.getAverageMount(
      originalFee - sumBy(this.splitOrderList, (item) => item.originalFee),
      count
    )
    const paidFeeArray = this.getAverageMount(
      paidFee - sumBy(this.splitOrderList, (item) => item.paidFee),
      count
    )
    const taxArray = this.getAverageMount(
      (tax || 0) - sumBy(this.splitOrderList, (item) => item.tax || 0),
      count
    )

    this.filterSplitList('Pending').forEach((item) => {
      const subOrderInfo = {
        ordersId: item.ordersId,
        originalFee: originalFeeArray[0],
        paidFee: paidFeeArray[0],
        tax: taxArray[0],
        subject: 'money',
        cnt: item.cnt,
        nono: item.nono,
      } as ISubOrder
      subOrderInfo.subOrdersDetail =
        item.subOrdersDetail || this.getEmptySubOrdersDetail()
      subOrderInfo.subOrdersDetail.forEach((item) => {
        item.weight = 0
        item.productExt = ''
      })
      if (item.seat) subOrderInfo.seat = item.seat
      subOrders.push(subOrderInfo)
    })

    const originalFeeCount = toDecimal(originalFeeArray[1] * 100)
    const paidFeeCount = toDecimal(paidFeeArray[1] * 100)
    const taxCount = toDecimal(taxArray[1] * 100)

    let i = 0
    while (i < originalFeeCount) {
      subOrders[i].originalFee = toDecimal(subOrders[i].originalFee + 0.01)
      i++
    }

    i = 0
    while (i < paidFeeCount) {
      subOrders[i].paidFee = toDecimal(subOrders[i].paidFee + 0.01)
      i++
    }

    // 多出来的税 ，那个少加在哪个上
    i = 0
    while (i < taxCount) {
      const min = minBy(
        subOrders,
        (item) => item.paidFee + (item.tax || 0)
      ) as ISubOrder
      min.tax = toDecimal((min.tax || 0) + 0.01)
      i++
    }

    this.filterSplitList('Pending').forEach((item, index) => {
      const subOrder = subOrders[index]
      subOrder.originalFee = toDecimal((item.originalFee || 0) + (subOrder.originalFee || 0))
      subOrder.paidFee = toDecimal((item.paidFee || 0) + (subOrder.paidFee || 0))
      subOrder.tax = toDecimal((item.tax || 0) + (subOrder.tax || 0))
    })

    this.setSubOrderDetailListFee(subOrders, false)

    try {
      const res = (await (SplitAPI as any).createUpdateSplitOrders({
        ordersId: this.mainOrderInfo.ordersId,
        addUersId: this.mainOrderInfo.addUserId,
        addUserName: this.mainOrderInfo.addUserName,
        subOrders,
      })) as any

      if (res?.result) {
        runInAction(() => {
          this.splitOrderList = this.sortListByNono(res?.result)
        })
        return res?.result
      } else {
        return Promise.reject(res.message)
      }
    } catch (error) {
      return Promise.reject(error.message)
    }
  }

  /** 修改子单金额 */
  async setSplitOrderFee (orderFees: IOrderFeeInfo[], isEdit?: boolean) {
    let itemDetail
    let findDetail
    let totals = {
      originalFeeTotal: sumBy(this.splitOrderList, (item) => item.originalFee || 0),
      paidFeeTotal: sumBy(this.splitOrderList, (item) => item.paidFee || 0),
      taxTotal: sumBy(this.splitOrderList, (item) => item.tax || 0),
    }
    const isExpress = some(orderFees, o => some(keys(o.details), d => this.productItemMap?.[d]?.type === 'express'))
    const isAutoSplitServiceCharge = this.isAutoSplitServiceCharge && !isExpress

    console.log('orderFees: =>', cloneDeep(orderFees))

    const subOrders = orderFees.map((item) => {
      const subOrder = this.returnSplitOrderFee({
        originalFee: item.originalFee,
        paidFee: item.paidFee,
        tax: item.tax || 0,
        index: item.index,
        totals,
        add: item.add
      }, isAutoSplitServiceCharge)

      console.log('subOrder: =>', cloneDeep(subOrder))

      if (item.details && subOrder.subOrdersDetail) {
        for (let detailId in item.details) {
          itemDetail = item.details[detailId]
          findDetail = subOrder.subOrdersDetail.find(
            (item) => detailId === item.detailId
          )
          if (findDetail) {
            findDetail.originalFee = toDecimal(itemDetail.originalFee || 0)
            findDetail.paidFee = toDecimal(itemDetail.paidFee || 0)
            findDetail.tax = toDecimal(itemDetail.tax || 0)
            findDetail.cnt = itemDetail.cnt || 1
            findDetail.weight = toDecimal(
              (item.add ? (findDetail.weight || 0) : 0) + (itemDetail.weight || 0)
            )
            findDetail.productExt = item.add ? fraction.add(itemDetail.productExt, findDetail.productExt) : itemDetail.productExt
          }
        }
      }

      if (isAutoSplitServiceCharge) {
        subOrder.subOrdersDetail
          ?.filter(item => item.itemType === 'serviceCharge')
          ?.forEach(item => {
            item.cnt = ~~!isEmpty(this.settedOrderItems)
            item.originalFee = 0
            item.paidFee = 0
            item.tax = 0
            item.weight = 0
          })
      }

      if (isEdit && subOrder.subOrdersDetail) {
        subOrder.subOrdersDetail.forEach((detail) => {
          detail.weight = 0
          detail.productExt = ''
        })
      }

      totals = {
        originalFeeTotal:
          (totals.originalFeeTotal || 0) - (this.splitOrderList?.[item.index]?.originalFee || 0) + (subOrder.originalFee || 0),
        paidFeeTotal:
          (totals.paidFeeTotal || 0) - (this.splitOrderList?.[item.index]?.paidFee || 0) + (subOrder.paidFee || 0),
        taxTotal:
          (totals.taxTotal || 0) - (this.splitOrderList?.[item.index]?.tax || 0) + (subOrder.tax || 0),
      }
      return subOrder
    })

    console.log(21, cloneDeep(subOrders))

    this.setSubOrderDetailListFee(subOrders, isAutoSplitServiceCharge)

    console.log(22, cloneDeep(subOrders))

    try {
      const res = (await (SplitAPI as any).createUpdateSplitOrders({
        ordersId: this.mainOrderInfo.ordersId,
        addUersId: this.mainOrderInfo.addUserId,
        addUserName: this.mainOrderInfo.addUserName,
        subOrders,
      })) as any

      if (res?.result) {
        runInAction(() => {
          this.splitOrderList = this.sortListByNono(res?.result)
        })
        return res?.result
      } else {
        return Promise.reject(res.message)
      }
    } catch (error) {
      return Promise.reject(error.message)
    }
  }

  returnSplitOrderFee ({ originalFee, paidFee, tax, index, totals, add }: {
    originalFee: number;
    paidFee: number;
    tax: number;
    index: number;
    totals: any;
    add?: boolean;
  }, isAutoSplitServiceCharge?: boolean) {
    const { originalFee: mainOriginalFee, paidFee: mainPaidFee, tax: mainTax, tip: mainTip, totalPrice } = this.mainOrderInfo
    const order = this.splitOrderList[index] as ISubOrder
    const orderInfo = {} as ISubOrder
    if (isAutoSplitServiceCharge) {
      order?.subOrdersDetail
        ?.filter(item => item.itemType === 'serviceCharge')
        ?.forEach(item => {
          order.originalFee = toDecimal((order.originalFee || 0) - (item.originalFee || 0))
          order.paidFee = toDecimal((order.paidFee || 0) - (item.paidFee || 0))
          order.tax = toDecimal((order.tax || 0) - (item.tax || 0))
        })
    }

    if (this.splitType === 'product') {
      if (add && paidFee) {
        orderInfo.paidFee = toDecimal(order.originalFee + originalFee)
        orderInfo.paidFee = toDecimal(order.paidFee + paidFee)
        orderInfo.tax = toDecimal(order.tax + tax)
      } else {
        orderInfo.originalFee = toDecimal(originalFee || 0)
        orderInfo.paidFee = toDecimal(paidFee || 0)
        orderInfo.tax = toDecimal(tax || 0)
      }
    } else {
      const realPaidFee = add ? (order.paidFee || 0) + (order.tax || 0) + paidFee + tax : paidFee + tax
      const weight = realPaidFee / (totalPrice - mainTip)
      orderInfo.originalFee = toDecimal(mainOriginalFee * weight)
      orderInfo.paidFee = toDecimal(mainPaidFee * weight)
      orderInfo.tax = toDecimal(realPaidFee - orderInfo.paidFee)
    }

    const diffOriginalFee = toDecimal(
      totals.originalFeeTotal -
        order.originalFee +
        orderInfo.originalFee -
        mainOriginalFee
    )

    const diffPaidFee = toDecimal(
      totals.paidFeeTotal -
        order.paidFee +
        orderInfo.paidFee -
        mainPaidFee
    )

    const diffTax = toDecimal(
      (totals.taxTotal || 0) -
      (order.tax || 0) +
      (orderInfo.tax || 0) -
      (mainTax || 0)
    )

    // 对于溢出的处理
    if (diffOriginalFee > 0) {
      orderInfo.originalFee = toDecimal(orderInfo.originalFee - diffOriginalFee)
    }

    if (diffPaidFee > 0) {
      orderInfo.paidFee = toDecimal(orderInfo.paidFee - diffPaidFee)
      orderInfo.tax = toDecimal((orderInfo.tax || 0) + diffPaidFee)
    }

    if (diffTax > 0) {
      orderInfo.paidFee = toDecimal(orderInfo.paidFee + diffTax)
      orderInfo.tax = toDecimal((orderInfo.tax || 0) - diffTax)
    }

    if (order.seat) orderInfo.seat = order.seat

    if (this.splitType === 'money') {
      order.subject = 'money'
    } else {
      order.subject = ''
    }

    return {
      ...orderInfo,
      cnt: order.cnt,
      nono: order.nono,
      subject: order.subject,
      ordersId: order.ordersId,
      subOrdersDetail: order.subOrdersDetail,
    }
  }

  /** 处理detail的fee(保证支付) */
  setSubOrderDetailListFee (subOrders: ISubOrder[], isAutoSplitServiceCharge: boolean) {
    const { totalPrice, express, tax, tip } = this.mainOrderInfo
    const { serviceCharge: mainServiceCharge, serviceChargeTax: mainServiceChargeTax } = this.mainServiceChargeInfo
    let restFee: any = { originalFee: 0, paidFee: 0, tax: 0 }
    let restServiceCharge = 0
    let restServiceChargeTax = 0

    if (mainServiceCharge) {
      if (isAutoSplitServiceCharge) {
        restServiceCharge = toDecimal(
          mainServiceCharge - mainServiceChargeTax -
            sumBy(this.splitOrderList, (item) => {
              if (item.subOrdersDetail) {
                return sumBy(item.subOrdersDetail?.filter(item => item.itemType === 'serviceCharge'), item => item?.paidFee || 0)
              }
              return 0
            })
        )
        restServiceChargeTax = toDecimal(
          mainServiceChargeTax -
            sumBy(this.splitOrderList, (item) => {
              if (item.subOrdersDetail) {
                return sumBy(item.subOrdersDetail?.filter(item => item.itemType === 'serviceCharge'), item => item?.tax || 0)
              }
              return 0
            })
        )
      } else {
        restServiceCharge = toDecimal(
          mainServiceCharge - mainServiceChargeTax -
            sumBy(this.filterSplitList('Success'), (item) => {
              if (item.subOrdersDetail) {
                return sumBy(item.subOrdersDetail?.filter(item => item.itemType === 'serviceCharge'), item => item?.paidFee || 0)
              }
              return 0
            })
        )
        restServiceChargeTax = toDecimal(
          mainServiceChargeTax -
            sumBy(this.filterSplitList('Success'), (item) => {
              if (item.subOrdersDetail) {
                return sumBy(item.subOrdersDetail?.filter(item => item.itemType === 'serviceCharge'), item => item?.tax || 0)
              }
              return 0
            })
        )
      }
    }

    // 分单数据处理
    subOrders.forEach((item) => {
      const serviceChargeDetails = item.subOrdersDetail?.filter(item => item.itemType === 'serviceCharge')
      const weightSubOrdersDetail = item.subOrdersDetail?.filter(item => item.itemType !== 'serviceCharge')?.find(item => !!item.weight) || item.subOrdersDetail?.[0]

      // 分单数据明细
      if (item.subOrdersDetail?.length) {
        if (restServiceCharge) {
          const serviceChargePer = toDecimal(item.paidFee || 0) / (isAutoSplitServiceCharge ? totalPrice - (mainServiceCharge - mainServiceChargeTax) - express - tip - tax : totalPrice - tip - tax)
          let serviceChargeFee = toDecimal(
            serviceChargePer * (mainServiceCharge - mainServiceChargeTax)
          )
          let serviceChargeTax = toDecimal(
            serviceChargePer * mainServiceChargeTax
          )
          if (serviceChargeFee > restServiceCharge) {
            serviceChargeFee = restServiceCharge
          }
          if (serviceChargeTax > restServiceChargeTax) {
            serviceChargeTax = restServiceChargeTax
          }
          restServiceCharge = toDecimal(restServiceCharge - serviceChargeFee)
          restServiceChargeTax = toDecimal(restServiceChargeTax - serviceChargeTax)
          restFee = {
            originalFee: toDecimal(serviceChargeFee),
            paidFee: toDecimal(serviceChargeFee),
            tax: toDecimal(serviceChargeTax),
          }
          if (isAutoSplitServiceCharge) {
            restFee.weight = toDecimal(serviceChargeFee + serviceChargeTax)
          } else {
            restFee.weight = 0
          }
          if (serviceChargeDetails?.length) {
            if (this.splitType === 'product' && isAutoSplitServiceCharge) {
              const serviceChargeList: any[] = this.productItemMap?.[this.mainOrderInfo.serviceChargeDetailId]?.detailList || []
              const serviceChargeMap = keyBy(serviceChargeList, 'detailId')
              const sumOriginalFee = sumBy(serviceChargeList, item => item.originalFee || 0)
              const sumPaidFee = sumBy(serviceChargeList, item => item.paidFee || 0)
              const sumTax = sumBy(serviceChargeList, item => item.tax || 0)
              serviceChargeDetails.forEach(serviceCharge => {
                const _originalFee = toDecimal(sumOriginalFee ? (serviceChargeMap?.[serviceCharge.detailId]?.originalFee || 0) / sumOriginalFee * restFee.originalFee : 0)
                const _paidFee = toDecimal(sumPaidFee ? (serviceChargeMap?.[serviceCharge.detailId]?.paidFee || 0) / sumPaidFee * restFee.paidFee : 0)
                const _tax = toDecimal(sumTax ? (serviceChargeMap?.[serviceCharge.detailId]?.tax || 0) / sumTax * restFee.tax : 0)
                const _restFee = {
                  originalFee: _originalFee,
                  paidFee: _paidFee,
                  tax: _tax,
                  weight: isAutoSplitServiceCharge ? toDecimal(_paidFee + _tax) : 0
                }
                Object.assign(serviceCharge, _restFee)
              })
            }
            if (this.splitType !== 'product') {
              const serviceChargeDetail = serviceChargeDetails.find(i => i.detailId === this.mainOrderInfo.serviceChargeDetailId)
              if (serviceChargeDetail) {
                Object.assign(serviceChargeDetail, restFee)
              }
            }
          }
        }

        // 按金额分单
        if (this.splitType !== 'product' && weightSubOrdersDetail) {
          weightSubOrdersDetail.originalFee = toDecimal(
            item.originalFee - restFee.originalFee
          )
          weightSubOrdersDetail.paidFee = toDecimal(
            item.paidFee - restFee.paidFee
          )
          weightSubOrdersDetail.tax = toDecimal(
            (item.tax || 0) - (restFee.tax || 0)
          )
        }

        // 已分单自动分服务费
        if (isAutoSplitServiceCharge && !!item.paidFee) {
          const hasProduct = some(item.subOrdersDetail?.filter(item => item.itemType !== 'serviceCharge'), item => !!item.weight || !!item.paidFee)
          if (hasProduct) {
            item.originalFee = toDecimal(item.originalFee + (isAutoSplitServiceCharge ? restFee.originalFee : 0))
            item.paidFee = toDecimal(item.paidFee + (isAutoSplitServiceCharge ? restFee.paidFee : 0))
            item.tax = toDecimal((item.tax || 0) + (isAutoSplitServiceCharge ? (restFee.tax || 0) : 0))
          } else {
            item.originalFee = 0
            item.paidFee = 0
            item.tax = 0
          }
        }
      }
    })

    // 按餐品分单数据处理
    if (this.splitType === 'product') {
      // 自动分服务费数据拉平
      const settedLen = values(this.settedOrderItems)?.filter(i => i.isFull)?.length
      const productLen = this.productItemList?.filter(i => i.paidFee > 0)?.length
      if (isAutoSplitServiceCharge && mainServiceCharge && settedLen === productLen - 1) {
        let lastItem = subOrders[subOrders.length - 1]
        let lastServiceCharge = lastItem.subOrdersDetail?.find(item => item.itemType === 'serviceCharge')
        if (lastServiceCharge) {
          let scAllPaidFee = 0
          let scAllTax = 0
          let list = this.splitOrderList
          list?.forEach(item => {
            item?.subOrdersDetail?.forEach((d: any) => {
              if (d.itemType === 'serviceCharge') {
                scAllPaidFee = toDecimal(scAllPaidFee + (d.paidFee || 0))
                scAllTax = toDecimal(scAllTax + (d.tax || 0))
              }
            })
          })
          const diffPaidFee = mainServiceCharge - mainServiceChargeTax - scAllPaidFee
          const diffTax = mainServiceChargeTax - scAllTax
          if (lastServiceCharge) {
            if (diffPaidFee !== 0) {
              lastServiceCharge.originalFee = toDecimal(lastServiceCharge.originalFee + diffPaidFee)
              lastServiceCharge.paidFee = toDecimal(lastServiceCharge.paidFee + diffPaidFee)
            }
            if (diffTax !== 0) {
              lastServiceCharge.tax = toDecimal(lastServiceCharge.tax + diffTax)
            }
            lastServiceCharge.weight = toDecimal((lastServiceCharge.paidFee || 0) + (lastServiceCharge.tax || 0))
          }
        }
      }

      // 分单详情明细数据拉平
      subOrders.forEach(item => {
        const _clacSubOrdersDetail = item.subOrdersDetail?.filter(d => !['FixedCombo', 'OptionalCombo'].includes(d.itemType))
        item.originalFee = toDecimal(_clacSubOrdersDetail?.reduce((t: number, i) => t + (i.originalFee || 0), 0) as number)
        item.paidFee = toDecimal(_clacSubOrdersDetail?.reduce((t: number, i) => t + (i.paidFee || 0), 0) as number)
        item.tax = toDecimal(_clacSubOrdersDetail?.reduce((t: number, i) => t + (i.tax || 0), 0) as number)
      })
    }
  }

  /** 获取子单详情 */
  async getSubOrderDetail (ordersId: string) {
    const res = (await (OrderAPI as any).get(ordersId)) as any
    if (res?.result) {
      return res?.result
    }
    return null
  }

  /** 取消分配餐品 */
  clearSplitOrderDetailWeight (detailId: string | number) {
    let findDetail: any
    const orderFess: IOrderFeeInfo[] = []
    const isExpress = this.productItemMap?.[detailId]?.type === 'express'
    const isAutoSplitServiceCharge = this.isAutoSplitServiceCharge && !isExpress
    this.splitOrderList.forEach((item, index) => {
      if (item.status === 'Pending') {
        const detailList = item.subOrdersDetail
          ?.filter(item => !['tip', 'surcharge'].includes(item.itemType))
        const productList = detailList?.filter(item => item.itemType !== 'serviceCharge')
        const serviceChargeList = detailList?.filter(item => item.itemType === 'serviceCharge')
        const productGroups: any = groupBy(productList, 'parentId')
        const serviceChargeMap = keyBy(serviceChargeList, 'detailId')
        findDetail = item.subOrdersDetail?.find(
          detail => detail.detailId === detailId
        )
        let details: any = {}
        let originalFee = toDecimal(item.originalFee || 0)
        let paidFee = toDecimal(item.paidFee || 0)
        let tax = toDecimal(item.tax || 0)
        let _detailList: ISubOrdersDetail[] = []
        let _childList: any = []
        if (findDetail) {
          // 餐品子项
          _childList = productGroups?.[findDetail.detailId] || []
          if (['FixedCombo', 'OptionalCombo'].includes(findDetail.itemType)) {
            _detailList = [cloneDeep(findDetail), ...cloneDeep(_childList)] as ISubOrdersDetail[]
            _childList.forEach((c: any) => {
              _detailList.push(...(cloneDeep((productGroups as any)?.[c.detailId]) || [] as ISubOrdersDetail[]))
            })
          } else {
            _detailList = [cloneDeep(findDetail), ...cloneDeep(_childList)] as ISubOrdersDetail[]
          }
          _detailList?.forEach(d => {
            details[d.detailId] = { originalFee: 0, paidFee: 0, tax: 0, weight: 0, productExt: '' }
          })

          // 服务费
          if (findDetail.itemType === 'serviceCharge' && findDetail.detailId === this.mainOrderInfo.serviceChargeDetailId) {
            serviceChargeList?.forEach((d: any) => {
              details[d.detailId] = { originalFee: 0, paidFee: 0, tax: 0, weight: 0, productExt: '' }
            })
          }
        }
        if (isAutoSplitServiceCharge && serviceChargeList?.length) {
          const detailListMap = keyBy(_detailList, 'detailId')
          const list = item.subOrdersDetail?.filter(
            detail => !detailListMap?.[detail.detailId] && !serviceChargeMap?.[detail.detailId]
          )
          serviceChargeList?.forEach((d: any) => {
            details[d.detailId] = { originalFee: 0, paidFee: 0, tax: 0, weight: 0, productExt: '' }
          })
          originalFee = sumBy(list, (detail: any) => detail.originalFee || 0)
          paidFee = sumBy(list, (detail: any) => detail.paidFee || 0)
          tax = sumBy(list, (detail: any) => detail.tax || 0)
        }
        if (findDetail) {
          orderFess.push({
            index,
            details,
            originalFee: toDecimal(originalFee),
            paidFee: toDecimal(paidFee),
            tax: toDecimal(tax)
          })
        }
      }
    })

    return this.setSplitOrderFee(orderFess)
  }

  removeDetailMount (
    item: ISubOrder,
    index: number,
    detailId: string,
    fee: number
  ) {
    const orderFess: IOrderFeeInfo[] = []
    const details = {} as any

    if (detailId !== 'other') {
      details[detailId] = { weight: 0, total: 0, productExt: '' }
    } else {
      item.subOrdersDetail?.forEach((detail) => {
        details[detail.detailId] = {
          cnt: detail.cnt,
          weight: detail.weight,
          total: detail.weight,
          productExt: '',
        }
      })
    }
    orderFess.push({
      index,
      details,
      originalFee: toDecimal(item.paidFee),
      paidFee: toDecimal(item.paidFee - fee),
      tax: toDecimal(item.tax || 0)
    })
    return this.setSplitOrderFee(orderFess)
  }

  setPayingCard (payingCard: string) {
    this.payingCard = payingCard
  }

  setSplitBySeat (isSplitBySeat: boolean) {
    this.isSplitBySeat = isSplitBySeat
  }

  setAutoSplitServiceCharge (isAutoSplitServiceCharge: boolean) {
    this.isAutoSplitServiceCharge = isAutoSplitServiceCharge
  }

  /** 再加一单 */
  async addOneOrder () {
    const res = (await (SplitAPI as any).createUpdateSplitOrders({
      subOrders: [{
        cnt: 0,
        originalFee: 0,
        paidFee: 0,
        tax: 0,
        status: 'Pending',
        parentId: null,
        nono: `${this.mainOrderInfo.nono}-${this.splitOrderList.length + 1}`,
        subOrdersDetail: this.getEmptySubOrdersDetail(),
      }],
      ordersId: this.mainOrderInfo.ordersId,
      addUersId: this.mainOrderInfo.addUserId,
      addUserName: this.mainOrderInfo.addUserName,
    })) as any
    if (res?.result) {
      runInAction(() => {
        this.splitOrderList = this.sortListByNono(res?.result)
      })
    }
  }

  /** 设置分单类型 */
  setSplitType (splitType: string) {
    this.splitType = splitType
  }
}
export default new SplitOrder()
